import { Outlet, Link, useLocation } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { AuthContext, GroupContext } from '@/App';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { 
  LayoutDashboard, 
  CheckSquare, 
  Users, 
  Calendar, 
  FileText, 
  User, 
  LogOut,
  Bell,
  FolderKanban,
  Shield,
  ClipboardList,
  HelpCircle,
  BookOpen,
  Gavel,
  ChevronDown,
  Building2,
  Check,
  Bug
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout() {
  const { user, logout } = useContext(AuthContext);
  const { groups, activeGroup, switchGroup } = useContext(GroupContext);
  const location = useLocation();
  const [systemSettings, setSystemSettings] = useState({ meetings_enabled: false });
  
  // Bug report dialog state
  const [showBugReport, setShowBugReport] = useState(false);
  const [bugReport, setBugReport] = useState({
    title: '',
    description: '',
    category: 'bug',
    priority: 'medium'
  });
  const [submittingBug, setSubmittingBug] = useState(false);

  useEffect(() => {
    // Fetch system settings
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/system/settings`);
        setSystemSettings(res.data);
      } catch (error) {
        console.error('Failed to fetch system settings');
      }
    };
    fetchSettings();
  }, []);

  const handleSubmitBugReport = async (e) => {
    e.preventDefault();
    if (!bugReport.title.trim() || !bugReport.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmittingBug(true);
    try {
      await axios.post(`${API}/support/tickets`, {
        ...bugReport,
        group_id: activeGroup?.id,
        group_name: activeGroup?.name,
        page_url: window.location.href,
        browser_info: navigator.userAgent
      });
      toast.success('Thank you! Your report has been submitted to the admin team.');
      setShowBugReport(false);
      setBugReport({ title: '', description: '', category: 'bug', priority: 'medium' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmittingBug(false);
    }
  };

  // Check if user has multiple groups (show selector only if > 1)
  const hasMultipleGroups = groups.length > 1;
  
  // Check if current group is NOT the default (show colored banner)
  const isNonDefaultGroup = activeGroup && !activeGroup.is_default;
  
  // Get the group color (from settings or default)
  const groupColor = activeGroup?.settings?.color || '#3B82F6';

  const handleGroupSwitch = async (groupId) => {
    if (groupId === activeGroup?.id) return;
    
    const success = await switchGroup(groupId);
    if (success) {
      const newGroup = groups.find(g => g.id === groupId);
      toast.success(`Switched to ${newGroup?.name || 'group'}`);
      // Reload page to refresh data for new group context
      window.location.reload();
    } else {
      toast.error('Failed to switch group');
    }
  };

  // Build navigation based on user role and feature flags
  const buildNavigation = () => {
    const nav = [
      { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { name: 'Actions', path: '/actions', icon: CheckSquare },
      { name: 'Decisions', path: '/decisions', icon: Gavel },
      { name: 'Reports', path: '/reports', icon: FileText },
    ];

    // Meetings visibility logic:
    // - If meetings_enabled: show to everyone (admins get management view)
    // - If meetings_disabled: show ONLY to super_admin (for testing/development)
    const isSuperAdmin = user?.role === 'super_admin';
    const isAdmin = user?.role === 'admin';
    const meetingsEnabled = systemSettings.meetings_enabled;

    if (meetingsEnabled || isSuperAdmin) {
      // Insert Meetings after Decisions
      const meetingsIcon = (isSuperAdmin || isAdmin) ? ClipboardList : Calendar;
      nav.splice(3, 0, { 
        name: meetingsEnabled ? 'Meetings' : 'Meetings (Dev)', 
        path: '/meeting-management', 
        icon: meetingsIcon 
      });
    }

    if (isSuperAdmin || isAdmin) {
      nav.push({ name: 'Categories', path: '/categories', icon: FolderKanban });
      nav.push({ name: 'Users', path: '/users', icon: Users });
    }

    // Super Admin only navigation
    if (isSuperAdmin) {
      nav.push({ name: 'Admin Tools', path: '/admin', icon: Shield });
    }

    return nav;
  };

  const navigation = buildNavigation();
  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Colored Group Banner - Only shown when in non-default group */}
      {isNonDefaultGroup && (
        <div 
          className="w-full py-2 px-4 text-white text-center text-sm font-medium flex items-center justify-center gap-2 shadow-sm"
          style={{ backgroundColor: groupColor }}
          data-testid="group-banner"
        >
          <Building2 className="w-4 h-4" />
          <span>You are working in: <strong>{activeGroup?.name}</strong></span>
          {hasMultipleGroups && (
            <span className="text-white/80 ml-2">
              (Click group selector to switch)
            </span>
          )}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <img 
              src="https://customer-assets.emergentagent.com/job_action-portal/artifacts/0jx7uwn8_HLRA_logo_non_transparent.jpeg" 
              alt="HLRA Logo" 
              className="h-16 w-auto mb-2"
            />
            <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">
              HLRA Actions
            </h1>
            <p className="text-xs text-gray-500 mt-1">Holborough Lakes</p>
            
            {/* Group Selector - Only shown if user has multiple groups */}
            {hasMultipleGroups && (
              <div className="mt-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-between text-left font-normal"
                      style={{ 
                        borderColor: groupColor,
                        borderWidth: '2px'
                      }}
                      data-testid="group-selector"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: groupColor }}
                        />
                        <span className="truncate">{activeGroup?.short_name || activeGroup?.name || 'Select Group'}</span>
                      </div>
                      <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>Switch Group</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {groups.map((group) => (
                      <DropdownMenuItem
                        key={group.id}
                        onClick={() => handleGroupSwitch(group.id)}
                        className="cursor-pointer"
                        data-testid={`group-option-${group.prefix}`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: group.settings?.color || '#3B82F6' }}
                            />
                            <span>{group.name}</span>
                            {group.is_default && (
                              <span className="text-xs text-gray-400">(Default)</span>
                            )}
                          </div>
                          {activeGroup?.id === group.id && (
                            <Check className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center mb-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-blue-100 text-blue-600">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">
                  {user?.role === 'super_admin' || user?.is_platform_admin 
                    ? 'Platform Admin' 
                    : activeGroup 
                      ? `${activeGroup.name} Member` 
                      : 'Group Member'}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Link to="/profile">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  data-testid="profile-button"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Button>
              </Link>
              <Link to="/help">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  data-testid="help-button"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Help & Guides
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                onClick={() => setShowBugReport(true)}
                data-testid="report-issue-button"
              >
                <Bug className="w-4 h-4 mr-2" />
                Report Issue
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={logout}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <div className="p-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bug Report Dialog */}
      <Dialog open={showBugReport} onOpenChange={setShowBugReport}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-orange-500" />
              Report an Issue
            </DialogTitle>
            <DialogDescription>
              Found a bug or have a suggestion? Let the admin team know.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitBugReport} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bug-title">Title *</Label>
              <Input
                id="bug-title"
                placeholder="Brief summary of the issue"
                value={bugReport.title}
                onChange={(e) => setBugReport({...bugReport, title: e.target.value})}
                required
                data-testid="bug-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={bugReport.category} 
                  onValueChange={(v) => setBugReport({...bugReport, category: v})}
                >
                  <SelectTrigger data-testid="bug-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">🐛 Bug</SelectItem>
                    <SelectItem value="feature">💡 Feature Request</SelectItem>
                    <SelectItem value="question">❓ Question</SelectItem>
                    <SelectItem value="other">📝 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={bugReport.priority} 
                  onValueChange={(v) => setBugReport({...bugReport, priority: v})}
                >
                  <SelectTrigger data-testid="bug-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">🟢 Low</SelectItem>
                    <SelectItem value="medium">🟡 Medium</SelectItem>
                    <SelectItem value="high">🔴 High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bug-description">Description *</Label>
              <Textarea
                id="bug-description"
                placeholder="Please describe what happened, what you expected, and steps to reproduce..."
                value={bugReport.description}
                onChange={(e) => setBugReport({...bugReport, description: e.target.value})}
                rows={4}
                required
                data-testid="bug-description-input"
              />
            </div>
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <p>Your current page and browser info will be included automatically.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowBugReport(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submittingBug}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="submit-bug-report"
              >
                {submittingBug ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}